import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '..';
import { tenantApi } from '../../api';
import { Tenant, TenantState, TenantTheme, UpdateTenantPayload } from '../../types';

const initialState: TenantState & { theme: TenantTheme | null } = {
  tenant: null,
  supportedCurrencies: null,
  baseCurrency: null,
  theme: null,
  loading: false,
  error: null,
  isInitialized: false,
};

export const fetchTenant = createAsyncThunk(
  'tenant/fetch',
  async (slug: string | undefined, { rejectWithValue }) => {
    try {
      let tenant;
      const domain = globalThis.location.hostname;

      try {
        // First try to get tenant by domain (supports custom domains)
        tenant = await tenantApi.getTenantByDomain(domain);
      } catch {
        // If domain lookup fails, fall back to slug
        if (slug) {
          tenant = await tenantApi.getTenantBySlug(slug);
        } else {
          throw new Error('No tenant found for this domain');
        }
      }

      return tenant.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch tenant data'
      );
    }
  }
);

export const updateTenant = createAsyncThunk(
  'tenant/update',
  async (
    { id, payload }: { id: string; payload: UpdateTenantPayload },
    { rejectWithValue }
  ) => {
    try {
      const response = await tenantApi.updateTenant(id, payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update tenant data'
      );
    }
  }
);

export const fetchSupportedCurrencies = createAsyncThunk(
  'tenant/fetchSupportedCurrencies',
  async (_, { rejectWithValue }) => {
    try {
      const response = await tenantApi.getSupportedCurrencies();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch supported currencies'
      );
    }
  }
);

export const fetchBaseCurrency = createAsyncThunk(
  'tenant/fetchBaseCurrency',
  async (_, { rejectWithValue }) => {
    try {
      const response = await tenantApi.getBaseCurrency();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch base currency'
      );
    }
  }
);

export const fetchTenantTheme = createAsyncThunk(
  'tenant/fetchTheme',
  async (_, { rejectWithValue }) => {
    try {
      return await tenantApi.getTenantTheme();
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch tenant theme'
      );
    }
  }
);

export const tenantSlice = createSlice({
  name: 'tenant',
  initialState,
  reducers: {
    clearTenant: (state) => {
      state.tenant = null;
      state.error = null;
      state.isInitialized = false;
    },
    setTenant: (state, action: PayloadAction<Tenant>) => {
      state.tenant = action.payload;
      state.error = null;
      state.isInitialized = true;
    },

    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTenant.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTenant.fulfilled, (state, action) => {
        state.loading = false;
        state.tenant = action.payload;
        state.error = null;
        state.isInitialized = true;
      })
      .addCase(fetchTenant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.isInitialized = true;
      })
      .addCase(updateTenant.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTenant.fulfilled, (state, action) => {
        state.loading = false;
        state.tenant = action.payload;
        state.error = null;
      })
      .addCase(updateTenant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchSupportedCurrencies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSupportedCurrencies.fulfilled, (state, action) => {
        state.loading = false;
        state.supportedCurrencies = action.payload;
        state.error = null;
      })
      .addCase(fetchSupportedCurrencies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchBaseCurrency.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBaseCurrency.fulfilled, (state, action) => {
        state.loading = false;
        state.baseCurrency = action.payload;
        state.error = null;
      })
      .addCase(fetchBaseCurrency.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchTenantTheme.fulfilled, (state, action) => {
        state.theme = action.payload;
      });
  },
});

export const tenantSelector = (state: RootState) => state.tenant;
export const selectTenant = (state: RootState) => state.tenant.tenant;
export const selectTenantLoading = (state: RootState) => state.tenant.loading;
export const selectTenantError = (state: RootState) => state.tenant.error;
export const selectTenantIsInitialized = (state: RootState) =>
  state.tenant.isInitialized;
export const selectSupportedCurrencies = (state: RootState) =>
  state.tenant.supportedCurrencies;
export const selectBaseCurrency = (state: RootState) =>
  state.tenant.baseCurrency;
export const selectTenantTheme = (state: RootState) =>
  (state.tenant as TenantState & { theme: TenantTheme | null }).theme;

export const { clearTenant, setTenant, clearError } = tenantSlice.actions;

export default tenantSlice.reducer;
